/**
 * TemplateVariableEditor - Wrapper for backwards compatibility.
 *
 * This component now wraps VariableDefinitionEditor and provides
 * conversion between TemplateVariableDefinition and VariableDefinitionModel.
 *
 * For new code, prefer using VariableDefinitionEditor directly.
 */

import {
	VariableFieldType,
	templateTypeToFieldType,
	type TemplateVariableDefinition,
	type VariableDefinitionModel,
} from '@supply-mover-2-0/shared';
import { useCallback } from 'react';
import { Card } from '../display/Card';
import {
	TYPE_OPTIONS,
	VariableDefinitionEditor,
	buildTypeOptions,
	buildTypeSearchGroups,
	getTypeValue,
	parseTypeValue,
	type TypeOption,
} from './VariableDefinitionEditor';

// Re-export utilities for backwards compatibility
export {
	TYPE_OPTIONS,
	buildTypeOptions,
	buildTypeSearchGroups,
	getTypeValue,
	parseTypeValue,
};
export type { TypeOption };

export interface TemplateVariableEditorProps {
	/** Current list of variables */
	variables: Array<TemplateVariableDefinition>;
	/** Callback when variables change */
	onChange: (variables: Array<TemplateVariableDefinition>) => void;
	/** Whether the editor is disabled (read-only) */
	disabled?: boolean;
	/** Title for the card header */
	title?: string;
	/** Whether to show the card wrapper */
	showCard?: boolean;
	/** Empty state message */
	emptyMessage?: string;
}

/**
 * Convert TemplateVariableDefinition to VariableDefinitionModel.
 */
function templateToModel(
	variable: TemplateVariableDefinition,
): VariableDefinitionModel {
	return {
		name: variable.name,
		type: templateTypeToFieldType(variable.type),
		required: variable.required,
		defaultValue: variable.defaultValue,
		description: variable.description,
		arrayType: variable.arrayType,
		objectType: variable.objectType,
	};
}

/**
 * Convert VariableDefinitionModel to TemplateVariableDefinition.
 */
function modelToTemplate(
	model: VariableDefinitionModel,
): TemplateVariableDefinition {
	// Map VariableFieldType back to TemplateVariableType
	let type: TemplateVariableDefinition['type'];

	switch (model.type) {
		case VariableFieldType.STRING:
		case VariableFieldType.EMAIL:
		case VariableFieldType.PHONE:
		case VariableFieldType.CURRENCY:
			type = 'string';
			break;
		case VariableFieldType.NUMBER:
			type = 'number';
			break;
		case VariableFieldType.BOOLEAN:
			type = 'boolean';
			break;
		case VariableFieldType.DATE:
			type = 'date';
			break;
		case VariableFieldType.ARRAY:
			type = 'array';
			break;
		case VariableFieldType.OBJECT:
			type = 'object';
			break;
		default:
			type = 'string';
	}

	return {
		name: model.name,
		type,
		required: model.required ?? false,
		defaultValue: model.defaultValue,
		description: model.description,
		arrayType: model.arrayType,
		objectType: model.objectType,
	};
}

/**
 * TemplateVariableEditor - A component for managing template variable definitions.
 *
 * Used by both email and messaging template editors to define variables
 * that can be inserted into templates using {{variableName}} syntax.
 *
 * This is now a thin wrapper around VariableDefinitionEditor that provides
 * backwards compatibility with the TemplateVariableDefinition interface.
 *
 * Features:
 * - Add/remove variables
 * - Edit variable name, type, required flag, and description
 * - Consistent styling with the design system
 */
export function TemplateVariableEditor({
	variables,
	onChange,
	disabled = false,
	title = 'Variables',
	showCard = true,
	emptyMessage = 'No variables defined',
}: TemplateVariableEditorProps) {
	// Convert TemplateVariableDefinition[] to VariableDefinitionModel[]
	const modelVariables = variables.map(templateToModel);

	// Handle changes and convert back to TemplateVariableDefinition[]
	const handleChange = useCallback(
		(models: Array<VariableDefinitionModel>) => {
			onChange(models.map(modelToTemplate));
		},
		[onChange],
	);

	const content = (
		<VariableDefinitionEditor
			variables={modelVariables}
			onChange={handleChange}
			disabled={disabled}
			title={title}
			showCard={false}
			emptyMessage={emptyMessage}
			showLabel={false}
			showDescription={true}
			showDefaultValue={false}
			showRequired={true}
			showImmutable={false}
			showPrimaryKey={false}
		/>
	);

	if (showCard) {
		return (
			<Card>
				<div className="p-6">{content}</div>
			</Card>
		);
	}

	return content;
}
